import { useSessionState } from '../../shared/hooks/session-state-hook';
import { useProfileState } from '../../shared/hooks/profile-state-hook';
import { sessionKey } from '../../shared/util/session-storage';
import PageHeading from '../../shared/components/UIElements/PageHeading';
import ProfileAvatar from '../../shared/components/UIElements/ProfileAvatar';
import Icon from '../../shared/components/UIElements/Icon';

import React, { useContext } from 'react';
import { AuthContext } from '../../shared/Context/auth-context';
import Card from '../../shared/components/UIElements/Card';
import Button from '../../shared/components/FormElements/Button';
import './PharmacyDashboard.css';
import { BACKEND_URL } from '../../shared/util/backend-url';

const PharmacyDashboard = () => {
  const auth = useContext(AuthContext);
  const pharmacy = auth.pharmacy;

  // ✅ Declare hooks BEFORE any conditionals
  const [editMode, setEditMode] = useSessionState(
    sessionKey(`pharmacy:${auth.pharmacyId}`, 'profile:editing'),
    false
  );
  const [formData, setFormData] = useProfileState(`pharmacy:${auth.pharmacyId}`, pharmacy, [
    'name',
    'email',
    'pharmacyName',
    'licenseNumber'
  ]);

  // ✅ Conditional render AFTER hooks
  if (!pharmacy) {
    return <p>Loading pharmacy data...</p>;
  }

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${BACKEND_URL}/api/pharmacy/${auth.pharmacyId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${auth.token}`
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          pharmacyName: formData.pharmacyName,
          licenseNumber: formData.licenseNumber
        })
      });

      const updated = await response.json();
      auth.setPharmacy(updated.updatedPharmacy);
      setEditMode(false);
    } catch (err) {
      console.error('Update failed:', err);
    }
  };

  return (
    <div className="pharmacy-dashboard">
      <PageHeading
        eyebrow="PHARMACY WORKSPACE"
        title={`Welcome back, ${pharmacy.name}.`}
        description="Keep your pharmacy connected to patient care."
      >
        <Button to={`/${auth.pharmacyId}/prescriptions`}>
          <Icon name="file" size={18} /> View prescriptions
        </Button>
      </PageHeading>
      <Card className="pharmacy-dashboard__card">
        <div className="panel-heading">
          <h2>Pharmacy profile</h2>
          <span className="badge">Pharmacy</span>
        </div>
        <div className="profile-identity">
          <ProfileAvatar
            name={pharmacy.name}
            src={
              pharmacy.profileImage ? `${BACKEND_URL}/${pharmacy.profileImage}` : undefined
            }
            className="profile-avatar--large"
          />
          <div>
            <h2>{pharmacy.pharmacyName || pharmacy.name}</h2>
            <p>{pharmacy.name}</p>
          </div>
        </div>
        {editMode ? (
          <form className="profile-form" onSubmit={handleSubmit}>
            <button
              type="button"
              className="modal-back full-width"
              onClick={() => setEditMode(false)}
            >
              <Icon name="back" size={17} />
              Back to profile
            </button>
            <label>
              Name:
              <input name="name" value={formData.name} onChange={handleChange} />
            </label>
            <label>
              Email:
              <input name="email" value={formData.email} onChange={handleChange} />
            </label>
            <label>
              Pharmacy Name:
              <input name="pharmacyName" value={formData.pharmacyName} onChange={handleChange} />
            </label>
            <label>
              License Number:
              <input name="licenseNumber" value={formData.licenseNumber} onChange={handleChange} />
            </label>
            <Button type="submit">Save</Button>
            <Button inverse type="button" onClick={() => setEditMode(false)}>
              Cancel
            </Button>
          </form>
        ) : (
          <>
            <dl className="detail-list detail-list--grid">
              <div>
                <dt>Pharmacy ID</dt>
                <dd>{pharmacy._id}</dd>
              </div>
              <div>
                <dt>Email address</dt>
                <dd>{pharmacy.email}</dd>
              </div>
              <div>
                <dt>Pharmacy name</dt>
                <dd>{pharmacy.pharmacyName}</dd>
              </div>
              <div>
                <dt>License number</dt>
                <dd>{pharmacy.licenseNumber}</dd>
              </div>
            </dl>
            <Button inverse onClick={() => setEditMode(true)}>
              Edit profile
            </Button>
          </>
        )}
      </Card>
    </div>
  );
};

export default PharmacyDashboard;
